/* eslint-disable react/jsx-no-constructed-context-values */
import React, { useEffect, useState } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { CssBaseline } from "@mui/material";
import * as Sentry from "@sentry/nextjs";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
// import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import mixpanel from "mixpanel-browser";
import { AppProps } from "next/app";
import dynamic from "next/dynamic";
import Head from "next/head";
import { useRouter } from "next/router";
import Script from "next/script";

import { ThemeProvider } from "@bxtech/bx-ui-lib";
import theme, { GlobalStyle } from "@bxtech/bx-ui-lib/styles/theme";

import "../globals.css";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import { ModalDataProvider } from "../context/ModalDataContext";
import AppErrorFallback from "@/components/AppErrorFallback";
import FrontendObservability from "@/components/FrontendObservability";
import GdprConsentModal from "@/components/GdprConsentModal";
import ProductTour from "@/components/ProductTour";
import { ConfirmationModalProvider } from "@/context/ConfirmationModalContext";
import { ConversationProvider } from "@/context/ConversationContext";
import {
  EnterpriseContext,
  EnterpriseProvider,
} from "@/context/EnterpriseContext";
import { I18nProvider } from "@/context/I18nContext";
import { PinnedItemsProvider } from "@/context/PinnedItemsContext";
import { SAMLayoutProvider } from "@/context/SAMLayoutContext";
import { SAMSidebarProvider } from "@/context/SAMSidebarContext";
import { SidebarMapProvider } from "@/context/SidebarMapContext";
import { SnackbarProvider } from "@/context/SnackbarProvider";
import { TourProvider } from "@/context/TourContext";
import { UploadFlowModalProvider } from "@/context/UploadFlowModalContext";
import { UserContext } from "@/context/UserContext";
import { useConsentGatedAnalytics } from "@/hooks/useConsentGatedAnalytics";
import { useGlobalErrorHandler } from "@/hooks/useGlobalErrorHandler";
import { useInitializeApp } from "@/hooks/useInitializeApp";
import { useLocation } from "@/hooks/useLocation";
import { useSecureRoute } from "@/hooks/useSecureRoutes";
import { UserType } from "@/types/api";

const DynamicAppSAMChatProvider = dynamic(
  () => import("../components/AppSAMChatProvider"),
  {
    ssr: false,
  }
);

const DynamicActiveTaskDetailsModal = dynamic(
  () => import("../components/ActiveTaskDetailsModal"),
  {
    loading: () => null, // Optional loading component
    ssr: false, // Disable server-side rendering for this component
  }
);

const DynamicActiveTaskSelectLocationModal = dynamic(
  () => import("../components/ActiveTaskSelectLocationModal"),
  {
    loading: () => null, // Optional loading component
    ssr: false, // Disable server-side rendering for this component
  }
);

const DynamicInActiveTaskDetailsModal = dynamic(
  () => import("../components/InactiveTaskDetailsModal"),
  {
    loading: () => null, // Optional loading component
    ssr: false, // Disable server-side rendering for this component
  }
);

const DynamicAddTaskModal = dynamic(
  () => import("../components/AddTaskModal"),
  {
    loading: () => null,
    ssr: false,
  }
);

const DynamicSAMContainer = dynamic(
  () => import("../components/SAMContainer"),
  {
    ssr: false,
  }
);

const DynamicSAMLegacyModal = dynamic(
  () =>
    import("../components/SAMLegacy").then((mod) => ({
      default: mod.SAMLegacyModal,
    })),
  {
    ssr: false,
  }
);

/**
 * Conditionally render SAM experience based on newHomepage feature flag:
 * - When newHomepage is ON: Show new SAMContainer (sidebar + minibar)
 * - When newHomepage is OFF: Show old SAMModal (bottom drawer / right drawer)
 */
function ConditionalSAMExperience() {
  const enterpriseContext = React.useContext(EnterpriseContext);
  const isNewHomepageEnabled = enterpriseContext?.featureToggles?.newHomepage;

  if (isNewHomepageEnabled) {
    // SAMContainer provides SAMChatProvider at app level for all pages.
    // On homepage, SAMContainerContent renders no UI (shouldShowSAM is false)
    // but the provider persists, preventing message loss during navigation.
    return <DynamicSAMContainer />;
  }

  // Old SAM experience: bottom drawer / right drawer (SAMLegacy)
  // SAMLayoutProvider is now at app level so pages can control layout
  return <DynamicSAMLegacyModal />;
}

// Add this declaration to inform TypeScript about the 'dataLayer' and 'hj' properties
declare global {
  interface Window {
    dataLayer: any[];
    hj?: (method: string, ...args: any[]) => void;
  }
}

function ConsentGatedAnalytics() {
  const { user } = React.useContext(UserContext);
  const { analyticsAllowed, experienceAllowed } = useConsentGatedAnalytics();
  const mixpanelInitialized = React.useRef(false);

  // Initialize Mixpanel - client-only, runs once (gated by analytics consent)
  useEffect(() => {
    if (!analyticsAllowed || mixpanelInitialized.current) return;
    if (typeof window !== "undefined") {
      const mixpanelToken = process.env.NEXT_PUBLIC_MIXPANEL_PROJECT_TOKEN;
      console.log(
        "[Mixpanel] Initializing with token:",
        mixpanelToken ? "Token present" : "Token missing"
      );
      if (mixpanel && mixpanel.init && mixpanelToken) {
        mixpanel.init(mixpanelToken, {
          debug: process.env.NEXT_PUBLIC_ENVIRONMENT !== "production",
          track_pageview: true,
          persistence: "localStorage",
        });
        mixpanelInitialized.current = true;
        console.log("[Mixpanel] Initialized successfully");
      } else {
        console.warn("[Mixpanel] Failed to initialize:", {
          hasMixpanel: !!mixpanel,
          hasInit: !!(mixpanel && mixpanel.init),
          hasToken: !!mixpanelToken,
        });
      }
    }
  }, [analyticsAllowed]);

  useEffect(() => {
    // Safely push user id to GTM dataLayer (gated by analytics consent)
    if (analyticsAllowed && typeof window !== "undefined") {
      window.dataLayer = window.dataLayer || [];
      if (user?.id) {
        window.dataLayer.push({ event: "set_user_id", user_id: user.id });
      }
    }

    // Identify the user in Mixpanel (gated by analytics consent)
    if (analyticsAllowed && mixpanel && mixpanel.identify && user?.id) {
      mixpanel.identify(user.id.toString());
      const userRole: any = user?.userRoles?.[0];
      const enterpriseName = userRole?.enterprise?.name;
      if (mixpanel.people) {
        mixpanel.people.set({
          $name: `${user?.firstName} ${user?.lastName}`,
          $email: user?.email,
          enterpriseName,
        });
      }
    }

    // Identify the user in Hotjar (gated by experience consent)
    if (experienceAllowed && window.hj && user?.id) {
      const userRole: any = user?.userRoles?.[0];
      const enterpriseName = userRole?.enterprise?.name;
      const roleName = userRole?.role?.name;
      window.hj("identify", user.id.toString(), {
        email: user.email || null,
        name: `${user.firstName || ""} ${user.lastName || ""}`.trim() || null,
        enterprise: enterpriseName || null,
        role: roleName || null,
      });
    }
  }, [user?.id, analyticsAllowed, experienceAllowed]);

  useEffect(() => {
    if (!experienceAllowed) return;
    // Only run on client side and not in E2E tests
    if (
      typeof window !== "undefined" &&
      process.env.NEXT_PUBLIC_E2E !== "true"
    ) {
      // Dynamically import usetiful-sdk
      import("usetiful-sdk").then(({ loadUsetifulScript }) => {
        loadUsetifulScript(process.env.NEXT_PUBLIC_USETFUL_TOKEN as string);
      });
    }
  }, [experienceAllowed]);

  return (
    <>
      {analyticsAllowed && (
        <Script id="google-tag-manager" strategy="afterInteractive">
          {`
          window.dataLayer = window.dataLayer || [];
          (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
          new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
          j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
          'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
        })(window,document,'script','dataLayer','${process.env.NEXT_PUBLIC_GOOGLE_ANALYTICS_ID}');
        `}
        </Script>
      )}
      {experienceAllowed && (
        <>
          <Script id="maze">
            {`
          (function (m, a, z, e) {
            var s, t;
            try {
              t = m.sessionStorage.getItem('maze-us');
            } catch (err) {}
            if (!t) {
              t = new Date().getTime();
              try {
                m.sessionStorage.setItem('maze-us', t);
              } catch (err) {}
            }
            s = a.createElement('script');
            s.src = z + '?apiKey=' + e;
            s.async = true;
            a.getElementsByTagName('head')[0].appendChild(s);
            m.mazeUniversalSnippetApiKey = e;
          })(window,document,'https://snippet.maze.co/maze-universal-loader.js','${process.env.NEXT_PUBLIC_MAZE_API_KEY}');
        `}
          </Script>
          {process.env.NEXT_PUBLIC_HOTJAR_ID && (
            <Script id="hotjar" strategy="afterInteractive">
              {`
            (function(h,o,t,j,a,r){
              h.hj=h.hj||function(){(h.hj.q=h.hj.q||[]).push(arguments)};
              h._hjSettings={hjid:${Number(
                process.env.NEXT_PUBLIC_HOTJAR_ID
              )},hjsv:6};
              a=o.getElementsByTagName('head')[0];
              r=o.createElement('script');r.async=1;
              r.src=t+h._hjSettings.hjid+j+h._hjSettings.hjsv;
              a.appendChild(r);
            })(window,document,'https://static.hotjar.com/c/hotjar-','.js?sv=');
            `}
            </Script>
          )}
        </>
      )}
      {analyticsAllowed && process.env.NEXT_PUBLIC_HUBSPOT_ID && (
        <Script
          id="hubspot"
          strategy="afterInteractive"
          src={`https://js-eu1.hs-scripts.com/${process.env.NEXT_PUBLIC_HUBSPOT_ID}.js`}
        />
      )}
    </>
  );
}

function GdprConsentGate() {
  const { user } = React.useContext(UserContext);
  const enterpriseContext = React.useContext(EnterpriseContext);
  const gdprEnabled = enterpriseContext?.featureToggles?.gdprEnabled;
  const consentAccepted = user?.states?.gdprConsentAccepted;

  if (!gdprEnabled || consentAccepted) return null;

  return <GdprConsentModal open />;
}

// Create wrapper function for loading state
function Wrapper({ children }: { children: React.ReactNode }) {
  const { user } = React.useContext(UserContext);
  const { loading } = useInitializeApp();

  // Only block rendering on initial load when there's no user yet
  // Once user is authenticated, allow instant navigation between pages
  if (loading && !user) {
    return <div />;
  }

  return <main style={{ overflow: "hidden" }}>{children}</main>;
}

// Create a client
const queryClient = new QueryClient();

function MyApp({ Component, pageProps }: AppProps) {
  const [user, setUser] = useState<UserType | null>(null);
  const router = useRouter();
  useGlobalErrorHandler();

  const { location, locationPermission } = useLocation();

  useSecureRoute(user!);

  return (
    <>
      <Head>
        <title>BX</title>
        <meta
          name="viewport"
          content="minimum-scale=1, initial-scale=1, width=device-width"
        />
        <link
          rel="icon"
          type="image/png"
          sizes="32x32"
          href={`${process.env.NEXT_PUBLIC_BX_CDN_URL}/favicon-32x32.png`}
        />
        <link
          rel="icon"
          type="image/png"
          sizes="16x16"
          href={`${process.env.NEXT_PUBLIC_BX_CDN_URL}/favicon-16x16.png`}
        />
      </Head>
      <QueryClientProvider client={queryClient}>
        <UserContext.Provider
          value={{
            user,
            setUser,
            location,
            locationPermission,
          }}
        >
          <I18nProvider user={user}>
            <FrontendObservability />
            <EnterpriseProvider>
              <ConsentGatedAnalytics />
              <TourProvider>
                <SAMSidebarProvider>
                  <SidebarMapProvider>
                    <ConversationProvider
                      enterpriseId={(router.query.enterpriseId as string) || ""}
                    >
                      <PinnedItemsProvider>
                        <ThemeProvider theme={theme}>
                          <CssBaseline />
                          <GlobalStyle />
                          <ErrorBoundary
                            FallbackComponent={AppErrorFallback}
                            onReset={() => {
                              router.push("/").catch((navigationError) => {
                                console.error(
                                  "Navigation error during reset:",
                                  navigationError
                                );
                                // Last resort - replace to avoid returning to the error page
                                window.location.replace("/");
                              });
                            }}
                            onError={(error, errorInfo) => {
                              // Enhanced error logging for Sentry
                              console.error("Error boundary caught error:", {
                                error,
                                errorInfo,
                              });

                              // Add additional context for Sentry
                              Sentry.captureException(error, {
                                tags: {
                                  component: "ErrorBoundary",
                                  errorType: "react_error_boundary",
                                },
                                extra: {
                                  errorInfo,
                                  // Avoid leaking tokens/PII via query/hash
                                  currentUrl: `${window.location.origin}${window.location.pathname}`,
                                  userAgent: navigator.userAgent,
                                  timestamp: new Date().toISOString(),
                                },
                              });
                            }}
                          >
                            <SnackbarProvider>
                              <ModalDataProvider>
                                <Wrapper>
                                  {user && (
                                    <>
                                      <GdprConsentGate />
                                      <DynamicAddTaskModal />
                                      {/* TODO: refactor to useTaskDetailsModal hook? */}
                                      <DynamicActiveTaskDetailsModal />
                                      <DynamicActiveTaskSelectLocationModal />
                                      <DynamicInActiveTaskDetailsModal />
                                    </>
                                  )}

                                  {/* SAMLayoutProvider wraps both SAM and pages so pages can control layout */}
                                  <SAMLayoutProvider>
                                    <ProductTour />
                                    <DynamicAppSAMChatProvider>
                                      {user && <ConditionalSAMExperience />}
                                      <ConfirmationModalProvider>
                                        <UploadFlowModalProvider>
                                          <Component {...pageProps} />
                                        </UploadFlowModalProvider>
                                      </ConfirmationModalProvider>
                                    </DynamicAppSAMChatProvider>
                                  </SAMLayoutProvider>
                                </Wrapper>
                              </ModalDataProvider>
                            </SnackbarProvider>
                          </ErrorBoundary>
                        </ThemeProvider>
                      </PinnedItemsProvider>
                    </ConversationProvider>
                  </SidebarMapProvider>
                </SAMSidebarProvider>
              </TourProvider>
            </EnterpriseProvider>
          </I18nProvider>
        </UserContext.Provider>
        {/* <ReactQueryDevtools initialIsOpen={false} position="bottom-left" /> */}
      </QueryClientProvider>
    </>
  );
}

export default MyApp;
